import React, { useEffect, useState, useMemo, useCallback } from "react";
import { UtensilsCrossed, Plus, Trash2, Search, Users, FileBarChart, X, Calendar } from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Button, Modal, Select, EmptyState } from "../lib/ui";
import { useToast } from "../lib/toast";
import { fmtRp, fmtNum, fmtUnit } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import EmployeeMealReport from "../components/EmployeeMealReport";

const today = () => new Date().toISOString().slice(0, 10);

export default function EmployeeMeal() {
  const { t } = useT();
  const { isRole, hasPermission } = useAuth();
  const isBranch = isRole("BRANCH_STAFF");
  const canCreate = hasPermission("create_employee_meal");
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [entries, setEntries] = useState(null);
  const [entryOpen, setEntryOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const load = useCallback(() => {
    const qs = !isBranch && branchFilter ? `?branch_id=${branchFilter}` : "";
    api.get(`/employee-meals${qs}`).then(({ data }) => setEntries(data.entries)).catch(() => setEntries([]));
  }, [isBranch, branchFilter]);

  useEffect(() => { if (!isBranch) api.get("/branches").then(({ data }) => setBranches(data)); }, [isBranch]);
  useEffect(() => { setEntries(null); load(); }, [load]);

  if (!entries) return <PageLoader />;

  return (
    <div className="animate-fade-up pb-24 lg:pb-2" data-testid="employee-meal-page">
      <PageHeader title={t("Employee Meal")} subtitle={t("Staff meals — legitimate internal consumption")} icon={UtensilsCrossed}>
        <Button variant="outline" onClick={() => setReportOpen(true)} data-testid="emeal-report-button"><FileBarChart size={18} /> {t("Report")}</Button>
        {canCreate && <Button onClick={() => setEntryOpen(true)} data-testid="emeal-add-button"><Plus size={18} /> {t("Record Meal")}</Button>}
      </PageHeader>

      {!isBranch && (
        <div className="mb-4 max-w-xs">
          <Select value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)} data-testid="emeal-branch-filter">
            <option value="">{t("All Branches")}</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
        </div>
      )}

      {entries.length === 0 ? (
        <EmptyState icon={UtensilsCrossed} title={t("No employee meals yet")} subtitle={t("Record staff meals to track internal consumption.")} />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="emeal-list">
          {entries.map((e) => (
            <Card key={e.id} className="p-4" data-testid={`emeal-card-${e.id}`}>
              <div className="mb-2 flex items-start justify-between">
                <div>
                  <p className="flex items-center gap-1.5 text-sm font-bold text-ink"><Calendar size={13} className="text-ink-mute" /> {e.date}</p>
                  {!isBranch && <p className="text-xs text-ink-mute">{e.branch_name}</p>}
                </div>
                <Badge tone="info"><Users size={12} /> {e.employee_count} {t("staff")}</Badge>
              </div>
              <div className="space-y-1 border-t border-line/60 pt-2">
                {e.items.map((it, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="text-ink-soft">{it.product_name}</span>
                    <span className="font-medium text-ink">{fmtNum(it.quantity)} {fmtUnit(it.unit)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex items-center justify-between border-t border-line/60 pt-2">
                <span className="text-xs text-ink-mute">{e.notes || "—"}</span>
                <span className="text-sm font-extrabold text-primary">{fmtRp(e.total_cost)}</span>
              </div>
            </Card>
          ))}
        </div>
      )}

      {entryOpen && <EntryModal branches={branches} isBranch={isBranch} onClose={() => setEntryOpen(false)} onSaved={() => { setEntryOpen(false); load(); }} />}
      <EmployeeMealReport open={reportOpen} onClose={() => setReportOpen(false)} />
    </div>
  );
}

function EntryModal({ branches, isBranch, onClose, onSaved }) {
  const { t } = useT();
  const toast = useToast();
  const [branchId, setBranchId] = useState(isBranch ? "" : (branches[0]?.id || ""));
  const [date, setDate] = useState(today());
  const [employees, setEmployees] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([]);          // [{product_id, name, unit, available, qty}]
  const [stock, setStock] = useState([]);
  const [search, setSearch] = useState("");
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);

  // Uses /my-inventory which is branch-scoped: branch staff -> own branch automatically,
  // HQ roles -> the selected branch. Avoids the central /branch-inventory (HQ-only) 403.
  useEffect(() => {
    if (!isBranch && !branchId) { setStock([]); return; }
    const qs = isBranch ? "" : `?branch_id=${branchId}`;
    api.get(`/branch/my-inventory${qs}`).then(({ data }) => setStock(data)).catch(() => setStock([]));
    setLines([]);
  }, [branchId, isBranch]);

  const available = useMemo(() => {
    const m = {};
    stock.forEach((s) => { m[s.product_id] = s.current_stock || 0; });
    return m;
  }, [stock]);

  const results = useMemo(() => {
    const q = search.trim().toLowerCase();
    const chosen = new Set(lines.map((l) => l.product_id));
    return stock
      .filter((s) => !chosen.has(s.product_id) && (s.current_stock || 0) > 0 && (!q || s.product_name.toLowerCase().includes(q)))
      .slice(0, 30);
  }, [stock, search, lines]);

  const addLine = (s) => {
    setLines((ls) => [...ls, { product_id: s.product_id, name: s.product_name, unit: s.unit, available: s.current_stock, qty: 1 }]);
    setSearch(""); setPicking(false);
  };
  const setQty = (pid, v) => setLines((ls) => ls.map((l) => l.product_id === pid ? { ...l, qty: v } : l));
  const removeLine = (pid) => setLines((ls) => ls.filter((l) => l.product_id !== pid));

  const overStock = lines.some((l) => Number(l.qty) > (available[l.product_id] ?? 0) + 1e-9);
  const canSave = (isBranch || branchId) && lines.length > 0 && lines.every((l) => Number(l.qty) > 0) && !overStock;

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/employee-meals", {
        date, branch_id: isBranch ? null : branchId, employee_count: Number(employees) || 0, notes,
        items: lines.map((l) => ({ product_id: l.product_id, quantity: Number(l.qty) })),
      });
      toast.success(t("Employee meal recorded"));
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} size="md" title={t("Record Employee Meal")}
      footer={<><Button variant="outline" onClick={onClose}>{t("Cancel")}</Button>
        <Button onClick={save} loading={saving} disabled={!canSave} data-testid="emeal-save">{t("Save")}</Button></>}>
      <div className="space-y-4">
        {!isBranch && (
          <div><label className="cf-label">{t("Branch")}</label>
            <Select value={branchId} onChange={(e) => setBranchId(e.target.value)} data-testid="emeal-branch">
              <option value="">{t("Select a branch")}</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </Select></div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div><label className="cf-label">{t("Date")}</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="cf-input" data-testid="emeal-date" /></div>
          <div><label className="cf-label">{t("Number of Employees")}</label>
            <input type="number" inputMode="numeric" value={employees} onChange={(e) => setEmployees(e.target.value)} className="cf-input" placeholder="0" data-testid="emeal-employees" /></div>
        </div>

        {/* item picker */}
        <div>
          <label className="cf-label">{t("Consumption Items")}</label>
          {lines.length > 0 && (
            <div className="mb-2 space-y-2" data-testid="emeal-lines">
              {lines.map((l) => {
                const over = Number(l.qty) > (available[l.product_id] ?? 0) + 1e-9;
                return (
                  <div key={l.product_id} className="rounded-xl border border-line p-2.5" data-testid={`emeal-line-${l.product_id}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-ink">{l.name}</span>
                      <button onClick={() => removeLine(l.product_id)} className="rounded-lg p-1 text-danger-text hover:bg-danger-light" data-testid={`emeal-remove-${l.product_id}`}><Trash2 size={15} /></button>
                    </div>
                    <div className="mt-1.5 flex items-center gap-2">
                      <input type="number" inputMode="decimal" value={l.qty} onChange={(e) => setQty(l.product_id, e.target.value)}
                        className={`cf-input flex-1 ${over ? "border-danger" : ""}`} data-testid={`emeal-qty-${l.product_id}`} />
                      <span className="w-10 text-sm text-ink-mute">{fmtUnit(l.unit)}</span>
                      <span className={`text-xs ${over ? "font-bold text-danger-text" : "text-ink-mute"}`}>
                        {over ? t("Stok tidak mencukupi") : `${t("avail")}: ${fmtNum(available[l.product_id] ?? 0)}`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {/* add item */}
          {picking ? (
            <div className="rounded-xl border border-primary/30 bg-primary-light/30 p-2" data-testid="emeal-picker">
              <div className="flex items-center gap-2 rounded-lg bg-white px-2">
                <Search size={15} className="text-ink-mute" />
                <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("Search product…")}
                  className="w-full bg-transparent py-2 text-sm outline-none" data-testid="emeal-search" />
                <button onClick={() => { setPicking(false); setSearch(""); }} className="text-ink-mute"><X size={16} /></button>
              </div>
              <div className="mt-1 max-h-48 overflow-auto">
                {results.length === 0 ? <p className="px-2 py-3 text-center text-xs text-ink-mute">{t("No products with stock")}</p> :
                  results.map((s) => (
                    <button key={s.product_id} onClick={() => addLine(s)} data-testid={`emeal-pick-${s.product_id}`}
                      className="flex w-full items-center justify-between rounded-lg px-2 py-2 text-left text-sm hover:bg-white">
                      <span className="font-medium text-ink">{s.product_name}</span>
                      <span className="text-xs text-ink-mute">{fmtNum(s.current_stock)} {fmtUnit(s.unit)}</span>
                    </button>
                  ))}
              </div>
            </div>
          ) : (
            <button onClick={() => setPicking(true)} disabled={!isBranch && !branchId} data-testid="emeal-add-item"
              className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-line py-3 text-sm font-semibold text-primary hover:bg-stone-50 disabled:opacity-50">
              <Plus size={16} /> {t("Add Item")}
            </button>
          )}
        </div>

        <div><label className="cf-label">{t("Notes")}</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="cf-input" placeholder={t("e.g. Lunch shift")} data-testid="emeal-notes" /></div>
      </div>
    </Modal>
  );
}
